(() => {
  const config = window.autoGiftConfig || {};
  const variantId = Number(config.variantId);
  const minimum = Number(config.minimum);

  if (!config.enabled || !Number.isFinite(variantId) || variantId <= 0 || !Number.isFinite(minimum) || minimum <= 0) {
    return;
  }

  const MARKER_KEY = '_auto_gift';
  const MARKER_VALUE = config.marker || 'bg_auto_gift_v1';
  const SOURCE = 'auto-gift';
  const cartUpdateEvent = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS.cartUpdate : null;
  const root = window.Shopify?.routes?.root || '/';
  const route = (value, fallback) => value || `${root}${fallback}`;
  const asJs = (url) => (url.endsWith('.js') ? url : `${url}.js`);
  const cartUrl = route(window.routes?.cart_url, 'cart');
  const addUrl = asJs(route(window.routes?.cart_add_url, 'cart/add'));
  const changeUrl = asJs(route(window.routes?.cart_change_url, 'cart/change'));

  let activePromise = null;
  let queued = false;
  let refreshTimer = null;

  const requestConfig = (body) => ({
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  const isAutoGift = (item) => item?.properties?.[MARKER_KEY] === MARKER_VALUE;

  async function responseJson(response) {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = data.description || data.message || `HTTP ${response.status}`;
      throw new Error(message);
    }
    return data;
  }

  async function fetchCart() {
    const response = await fetch(asJs(cartUrl), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    return responseJson(response);
  }

  async function addGift() {
    const response = await fetch(
      addUrl,
      requestConfig({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              [MARKER_KEY]: MARKER_VALUE,
            },
          },
        ],
      })
    );
    return responseJson(response);
  }

  async function changeLine(key, quantity) {
    const response = await fetch(changeUrl, requestConfig({ id: key, quantity }));
    return responseJson(response);
  }

  function cartSubtotalWithoutGift(cart) {
    return (cart?.items || []).reduce((total, item) => {
      if (isAutoGift(item)) return total;
      const value = Number(item.original_line_price ?? item.line_price ?? 0);
      return total + (Number.isFinite(value) ? value : 0);
    }, 0);
  }

  function currentGift(cart) {
    return (cart?.items || []).find((item) => isAutoGift(item) && Number(item.variant_id) === variantId) || null;
  }

  async function removeStaleGiftLines(cart) {
    const giftLines = (cart?.items || []).filter(isAutoGift);
    let keptCurrent = false;
    let changed = false;

    for (const item of giftLines) {
      const isCurrentVariant = Number(item.variant_id) === variantId;
      if (isCurrentVariant && !keptCurrent) {
        keptCurrent = true;
        continue;
      }

      await changeLine(item.key, 0);
      changed = true;
    }

    return changed;
  }

  function giftIsFree(item) {
    if (!item) return false;
    return Number(item.final_line_price ?? item.line_price ?? 0) === 0;
  }

  async function refreshSmallCartUi() {
    try {
      const separator = cartUrl.includes('?') ? '&' : '?';
      const response = await fetch(
        `${cartUrl}${separator}sections=cart-icon-bubble,cart-notification-button`,
        { headers: { Accept: 'application/json' }, cache: 'no-store' }
      );
      const sections = await responseJson(response);

      const updateSectionContents = (targetSelector, html) => {
        const target = document.querySelector(targetSelector);
        if (!target || !html) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const wrapper = doc.querySelector('.shopify-section');
        target.innerHTML = wrapper ? wrapper.innerHTML : doc.body.innerHTML;
      };

      updateSectionContents('#cart-icon-bubble', sections['cart-icon-bubble']);
      updateSectionContents('#cart-notification-button', sections['cart-notification-button']);
    } catch (error) {
      console.warn('[Brinde automático] Não foi possível atualizar o contador visual do carrinho.', error);
    }
  }

  function scheduleUiRefresh(cart) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(async () => {
      await refreshSmallCartUi();

      if (typeof publish === 'function' && cartUpdateEvent) {
        publish(cartUpdateEvent, {
          source: SOURCE,
          cartData: cart,
          productVariantId: variantId,
        });
      }

      document.dispatchEvent(
        new CustomEvent('auto-gift:updated', {
          detail: { cart },
        })
      );
    }, 0);
  }

  async function reconcileOnce() {
    let cart = await fetchCart();
    let mutated = false;

    if (await removeStaleGiftLines(cart)) {
      mutated = true;
      cart = await fetchCart();
    }

    let gift = currentGift(cart);
    const qualifyingSubtotal = cartSubtotalWithoutGift(cart);

    if (qualifyingSubtotal < minimum) {
      if (gift) {
        cart = await changeLine(gift.key, 0);
        mutated = true;
      }

      if (mutated) scheduleUiRefresh(cart);
      return cart;
    }

    if (!gift) {
      try {
        await addGift();
        mutated = true;
        cart = await fetchCart();
        gift = currentGift(cart);
      } catch (error) {
        console.warn('[Brinde automático] O produto de brinde não pôde ser adicionado.', error);
        return cart;
      }
    }

    if (gift && Number(gift.quantity) !== 1) {
      cart = await changeLine(gift.key, 1);
      mutated = true;
      gift = currentGift(cart);
    }

    // A Shopify é a fonte de verdade para as regras do desconto. Assim, mesmo
    // que o carrinho passe do valor mínimo do tema, o item só permanece se o
    // desconto automático realmente tiver zerado o preço do brinde.
    if (config.verifyFree !== false && gift && !giftIsFree(gift)) {
      cart = await changeLine(gift.key, 0);
      mutated = true;
      gift = null;
    }

    if (mutated) scheduleUiRefresh(cart);
    return cart;
  }

  function reconcile() {
    if (activePromise) {
      queued = true;
      return activePromise;
    }

    activePromise = reconcileOnce()
      .catch((error) => {
        console.warn('[Brinde automático] Falha ao sincronizar o brinde.', error);
        return null;
      })
      .finally(() => {
        activePromise = null;
        if (queued) {
          queued = false;
          window.setTimeout(reconcile, 0);
        }
      });

    return activePromise;
  }

  if (typeof subscribe === 'function' && cartUpdateEvent) {
    subscribe(cartUpdateEvent, (event) => {
      if (event?.source === SOURCE) return;
      return reconcile();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile, { once: true });
  } else {
    reconcile();
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) reconcile();
  });
})();
